import React, { useState, useEffect, useMemo } from 'react';
import { 
  Table, Card, Badge, Spinner, Alert, Button, Modal, 
  Form, Row, Col, Tab, Tabs, InputGroup 
} from 'react-bootstrap';
import { userService } from '../../services/api';
import { format } from 'date-fns';
import Pagination from '../Pagination';
import './UserManagement.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  
  // Modals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  
  // Selected user
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // New role
  const [newRole, setNewRole] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [users, searchTerm, roleFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getAllUsers();
      setUsers(response.data.users ?? response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  const handleViewDetails = async (user) => {
    setSelectedUser(user);
    setShowDetailsModal(true);
    setLoadingDetails(true);
    
    try {
      const response = await userService.getUserDetails(user._id);
      setUserDetails(response.data);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Failed to load user details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleChangeRole = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowRoleModal(true);
  };

  const handleConfirmRoleChange = async () => {
    try {
      await userService.updateUserRole(selectedUser._id, newRole);
      setShowRoleModal(false);
      fetchUsers();
      setError(null);
    } catch (err) {
      console.error('Error updating role:', err);
      setError(err.response?.data?.message || 'Failed to update user role');
    }
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await userService.deleteUser(selectedUser._id);
      setShowDeleteModal(false);
      fetchUsers();
      setError(null);
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  // Paginated users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0">User Management</h3>
        <Badge bg="primary" pill className="fs-6">
          {filteredUsers.length} {filteredUsers.length === 1 ? 'user' : 'users'}
        </Badge>
      </div>
      
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Search Users</Form.Label>
                <InputGroup>
                  <InputGroup.Text>
                    <i className="bi bi-search"></i>
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder="Search by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>Filter by Role</Form.Label>
                <Form.Select 
                  value={roleFilter} 
                  onChange={(e) => setRoleFilter(e.target.value)}
                >
                  <option value="all">All Roles</option>
                  <option value="admin">Admins</option>
                  <option value="user">Regular Users</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      
      {filteredUsers.length === 0 ? (
        <Card className="text-center p-4">
          <Card.Body>
            <i className="bi bi-people fs-1 text-muted mb-3 d-block"></i>
            <p className="mb-0">No users found matching your criteria.</p>
          </Card.Body>
        </Card>
      ) : (
        <Card>
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                <th>Certification</th>
                <th>Dives</th>
                <th>Joined</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.map(user => (
                <tr key={user._id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="user-avatar me-2">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <strong>{user.name}</strong>
                    </div>
                  </td>
                  <td className="text-muted">{user.email}</td>
                  <td>
                    <Badge bg={user.role === 'admin' ? 'primary' : 'secondary'}>
                      {user.role === 'admin' ? '👑 Admin' : 'User'}
                    </Badge>
                  </td>
                  <td>{user.certificationLevel || 'N/A'}</td>
                  <td>{user.numberOfDives || 0}</td>
                  <td className="text-muted small">
                    {format(new Date(user.createdAt), 'MMM d, yyyy')}
                  </td>
                  <td className="text-end">
                    <div className="btn-group btn-group-sm">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleViewDetails(user)}
                        title="View Details"
                      >
                        <i className="bi bi-eye"></i>
                      </Button>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        onClick={() => handleChangeRole(user)}
                        title="Change Role"
                      >
                        <i className="bi bi-shield"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDeleteUser(user)}
                        title="Delete User"
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {filteredUsers.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          totalItems={filteredUsers.length}
          itemsPerPage={itemsPerPage}
        />
      )}

      {/* User Details Modal */}
      <Modal 
        show={showDetailsModal} 
        onHide={() => setShowDetailsModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-person-circle me-2"></i>
            User Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingDetails ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
              <p className="mt-2">Loading user details...</p>
            </div>
          ) : userDetails ? (
            <Tabs defaultActiveKey="info" className="mb-3">
              <Tab eventKey="info" title="Information">
                <Row>
                  <Col md={6}>
                    <div className="detail-item">
                      <strong>Name:</strong>
                      <p>{userDetails.user.name}</p>
                    </div>
                    <div className="detail-item">
                      <strong>Email:</strong>
                      <p>{userDetails.user.email}</p>
                    </div>
                    <div className="detail-item">
                      <strong>Role:</strong>
                      <p>
                        <Badge bg={userDetails.user.role === 'admin' ? 'primary' : 'secondary'}>
                          {userDetails.user.role}
                        </Badge>
                      </p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="detail-item">
                      <strong>Certification Level:</strong>
                      <p>{userDetails.user.certificationLevel || 'Not specified'}</p>
                    </div>
                    <div className="detail-item">
                      <strong>Number of Dives:</strong>
                      <p>{userDetails.user.numberOfDives || 0}</p>
                    </div>
                    <div className="detail-item">
                      <strong>Member Since:</strong>
                      <p>{format(new Date(userDetails.user.createdAt), 'MMMM d, yyyy')}</p>
                    </div>
                  </Col>
                </Row>

                <hr />

                <h6>Statistics</h6>
                <Row className="text-center">
                  <Col xs={4}>
                    <div className="stat-box">
                      <div className="stat-value text-primary">{userDetails.stats.totalApplications}</div>
                      <div className="stat-label">Total Applications</div>
                    </div>
                  </Col>
                  <Col xs={4}>
                    <div className="stat-box">
                      <div className="stat-value text-success">{userDetails.stats.approvedApplications}</div>
                      <div className="stat-label">Approved</div>
                    </div>
                  </Col>
                  <Col xs={4}>
                    <div className="stat-box">
                      <div className="stat-value text-info">{userDetails.stats.tripsCreated}</div>
                      <div className="stat-label">Trips Created</div>
                    </div>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="applications" title={`Applications (${userDetails.applications.length})`}>
                {userDetails.applications.length === 0 ? (
                  <p className="text-muted">No applications yet.</p>
                ) : (
                  <Table hover size="sm">
                    <thead>
                      <tr>
                        <th>Trip</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userDetails.applications.map(app => (
                        <tr key={app._id}>
                          <td>
                            <div>{app.tripId?.name || 'N/A'}</div>
                            <small className="text-muted">{app.tripId?.location}</small>
                          </td>
                          <td>
                            <Badge bg={
                              app.status === 'approved' ? 'success' :
                              app.status === 'pending' ? 'warning' :
                              app.status === 'waitingList' ? 'info' : 'danger'
                            }>
                              {app.status}
                            </Badge>
                          </td>
                          <td className="text-muted small">
                            {format(new Date(app.createdAt), 'MMM d, yyyy')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Tab>

              {userDetails.createdTrips.length > 0 && (
                <Tab eventKey="trips" title={`Trips Created (${userDetails.createdTrips.length})`}>
                  <Table hover size="sm">
                    <thead>
                      <tr>
                        <th>Trip Name</th>
                        <th>Location</th>
                        <th>Status</th>
                        <th>Start Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userDetails.createdTrips.map(trip => (
                        <tr key={trip._id}>
                          <td>{trip.name}</td>
                          <td>{trip.location}</td>
                          <td>
                            <Badge bg={
                              trip.status === 'upcoming' ? 'primary' :
                              trip.status === 'ongoing' ? 'success' :
                              trip.status === 'completed' ? 'secondary' : 'danger'
                            }>
                              {trip.status}
                            </Badge>
                          </td>
                          <td className="text-muted small">
                            {format(new Date(trip.startDate), 'MMM d, yyyy')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Tab>
              )}
            </Tabs>
          ) : null}
        </Modal.Body>
      </Modal>

      {/* Change Role Modal */}
      <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Change User Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p>
                Change role for <strong>{selectedUser.name}</strong>?
              </p>
              <Form.Group>
                <Form.Label>Select Role</Form.Label>
                <Form.Select 
                  value={newRole} 
                  onChange={(e) => setNewRole(e.target.value)}
                >
                  <option value="user">Regular User</option>
                  <option value="admin">Admin</option>
                </Form.Select>
              </Form.Group>
              <Alert variant="warning" className="mt-3 mb-0">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Note:</strong> Admins have full access to manage trips, users, and applications.
              </Alert>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRoleModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRoleChange}>
            Update Role
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete User Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Delete User
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <Alert variant="danger">
                <strong>Warning:</strong> This action cannot be undone!
              </Alert>
              <p>
                Are you sure you want to delete <strong>{selectedUser.name}</strong>?
              </p>
              <p className="text-muted small">
                This will delete the user and all their applications. If they have created trips,
                you'll need to delete or reassign those first.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            <i className="bi bi-trash me-2"></i>
            Delete User
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserManagement;
